import { HStack, Stack, VStack } from "@chakra-ui/react"
import React from "react"

import BlurFade from "@/components/Animations/BlurFade"
import ErrorMessage from "@/components/ErrorMessage"
import MarkdownParser from "@/components/MarkdownParser"
import { type FigmaBlock as FigmaBlockType } from "@/types/database"
import getStickyContentBlockLeftProps from "@/utils/getStickyContentBlockLeftProps"

import ContentBlockTitle, {
  type ContentBlockTitleNumberProps,
} from "./ContentBlockTitle"

type FigmaBlockProps = ContentBlockTitleNumberProps & {
  block: FigmaBlockType
}

function getEmbedUrl(url: string): string | null {
  // Figma file: https://www.figma.com/file/{fileKey}/...
  // Figma prototype: https://www.figma.com/proto/{fileKey}/...
  const fileMatch = url.match(
    /^https:\/\/www\.figma\.com\/file\/([^/?#]+)(.*)$/
  )
  const protoMatch = url.match(
    /^https:\/\/www\.figma\.com\/proto\/([^/?#]+)(.*)$/
  )

  if (fileMatch) {
    return `https://www.figma.com/embed?embed_host=share&url=${encodeURIComponent(url)}`
  }
  if (protoMatch) {
    return `https://www.figma.com/embed?embed_host=share&url=${encodeURIComponent(url)}`
  }
  return null
}

const FigmaBlock: React.FC<FigmaBlockProps> = ({ block, titleNumber }) => {
  const { description, title, two_columns_figma, url } = block

  const embedUrl = getEmbedUrl(url)

  if (!embedUrl) {
    return <ErrorMessage status="error" title="Invalid Figma link." />
  }

  const trimmedTitle = title?.trim()
  const hasTitle =
    trimmedTitle !== undefined && trimmedTitle !== "" && trimmedTitle !== null
  const hasDescription =
    description !== undefined || description !== "" || description !== null
  return (
    <Stack
      alignItems="flex-start"
      className="figma-block  edge-to-edge"
      direction={{
        base: "column",
        lg: two_columns_figma ? "row" : "column",
      }}
      gap={{ base: 8, lg: two_columns_figma ? 6 : 8 }}
      justifyContent="flex-end"
      px={{ base: 8, md: 10, lg: 24, xl: 40 }}
    >
      {(hasTitle || hasDescription) && (
        <VStack
          alignItems="flex-start"
          flexGrow={1}
          maxWidth={{
            base: "100%",
            lg: two_columns_figma
              ? title !== undefined
                ? "30%"
                : "100%"
              : "100%",
          }}
          pt={{ base: 12, lg: 24 }}
          {...getStickyContentBlockLeftProps(Boolean(two_columns_figma))}
        >
          <BlurFade flexDirection="column" gap={0} inView>
            <ContentBlockTitle title={trimmedTitle} titleNumber={titleNumber} />
            {hasDescription && (
              <MarkdownParser
                color="fg.muted"
                fontSize="sm"
                markdown={description}
                pProps={{ fontFamily: "mono" }}
              />
            )}
          </BlurFade>
        </VStack>
      )}
      <VStack
        alignItems="flex-start"
        flexBasis={{ base: "auto", lg: "0" }}
        flexGrow={1}
        maxWidth={{
          base: "100%",
          lg: block.two_columns_figma
            ? block.title !== undefined
              ? "70%"
              : "100%"
            : "100%",
        }}
        pl={!hasTitle && two_columns_figma ? { base: 0, lg: 12 } : 0}
      >
        <BlurFade inView>
          <HStack
            alignItems="flex-start"
            bgColor="bg.subtle"
            borderColor="border"
            borderRadius="3xl"
            borderWidth="1px"
            data-print-figma-frame=""
            flexGrow={1}
            gap={4}
            height={"450px"}
            p={6}
            width={"100%"}
            wrap="wrap"
          >
            <iframe
              allowFullScreen
              height="100%"
              src={embedUrl}
              style={{ border: "none" }}
              title="Figma Embed"
              width="100%"
            />
          </HStack>
        </BlurFade>
      </VStack>
    </Stack>
  )
}

export default FigmaBlock
